import { useQuery } from "@apollo/client";
import {
	Anchor,
	Card,
	Container,
	Group,
	Loader,
	MultiSelect,
	Pagination,
	ScrollArea,
	Select,
	SimpleGrid,
	Stack,
	Table,
	Text,
	TextInput,
	Title,
	Tooltip,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useDebouncedValue } from "@mantine/hooks";
import { IconRobot, IconSearch } from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import type {
	EmployeeAiUsageFilter,
	EmployeeGetAiUsageQuery,
	EmployeeGetOrganizationsQuery,
} from "../../graphql/generated/types.gen";
import {
	EMPLOYEE_GET_AI_USAGE,
	EMPLOYEE_GET_ORGANIZATIONS,
} from "../../graphql/queries";
import { useAuthCompat } from "../../hooks/useAuthCompat";
import { useIsMobile } from "../../hooks/useIsMobile";

export const Route = createFileRoute("/admin/ai-usage")({
	component: AdminAiUsagePage,
});

const PAGE_SIZE = 20;

function formatCost(value: number | null | undefined): string {
	return `$${(value ?? 0).toFixed(2)}`;
}

function formatNumber(value: number | null | undefined): string {
	return (value ?? 0).toLocaleString();
}

function AdminAiUsagePage() {
	const { user, isLoading: authLoading } = useAuthCompat();
	const isMobile = useIsMobile();

	const [search, setSearch] = useState("");
	const [debouncedSearch] = useDebouncedValue(search, 300);
	const [page, setPage] = useState(1);
	const [dateRange, setDateRange] = useState<[string | null, string | null]>([
		null,
		null,
	]);
	const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
	const [orgSearch, setOrgSearch] = useState("");
	const [debouncedOrgSearch] = useDebouncedValue(orgSearch, 300);
	const [selectedOperations, setSelectedOperations] = useState<string[]>([]);

	useEffect(() => {
		if (authLoading) return;
		if (!user) {
			window.location.href = "/welcome";
			return;
		}
		if (!user.isEmployee) {
			window.location.href = "/";
		}
	}, [user, authLoading]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Reset page when any filter changes
	useEffect(() => {
		setPage(1);
	}, [debouncedSearch, selectedOrgId, selectedOperations, dateRange]);

	const { data: orgData } = useQuery<EmployeeGetOrganizationsQuery>(
		EMPLOYEE_GET_ORGANIZATIONS,
		{
			variables: {
				search: debouncedOrgSearch || undefined,
				limit: 50,
			},
			skip: !user?.isEmployee,
		},
	);

	const orgOptions = useMemo(
		() =>
			(orgData?.employeeOrganizations.organizations ?? []).map((o) => ({
				value: o.id,
				label: o.name,
			})),
		[orgData],
	);

	const filter = useMemo((): EmployeeAiUsageFilter | undefined => {
		const f: EmployeeAiUsageFilter = {};
		if (debouncedSearch) f.search = debouncedSearch;
		if (selectedOrgId) f.organizationId = selectedOrgId;
		if (selectedOperations.length > 0) f.operation = selectedOperations;
		if (dateRange[0]) f.startDate = dateRange[0];
		if (dateRange[1]) f.endDate = dateRange[1];
		return Object.keys(f).length > 0 ? f : undefined;
	}, [debouncedSearch, selectedOrgId, selectedOperations, dateRange]);

	const { data, loading } = useQuery<EmployeeGetAiUsageQuery>(
		EMPLOYEE_GET_AI_USAGE,
		{
			variables: {
				filter,
				limit: PAGE_SIZE,
				offset: (page - 1) * PAGE_SIZE,
			},
			skip: !user?.isEmployee,
		},
	);

	// Extract distinct operations from results for the MultiSelect options
	const operationOptions = useMemo(() => {
		const ops = new Set<string>();
		for (const item of data?.employeeAiUsage.items ?? []) {
			ops.add(item.operation);
		}
		// Include currently selected operations so they remain visible
		for (const op of selectedOperations) {
			ops.add(op);
		}
		return [...ops].sort().map((op) => ({ value: op, label: op }));
	}, [data, selectedOperations]);

	if (authLoading || !user?.isEmployee) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<Loader size="lg" />
			</div>
		);
	}

	const items = data?.employeeAiUsage.items ?? [];
	const totalCount = data?.employeeAiUsage.totalCount ?? 0;
	const totalPages = Math.ceil(totalCount / PAGE_SIZE);
	const summary = data?.employeeAiUsage.summary;

	return (
		<div className="h-[calc(100vh-var(--header-height))] overflow-auto bg-[var(--mantine-color-body)]">
			<Container size="lg" py="xl" px={isMobile ? "md" : "xl"}>
				<Group gap="md" mb="xs" wrap="nowrap">
					<IconRobot
						size={isMobile ? 24 : 32}
						color="var(--mantine-color-blue-6)"
					/>
					<Title order={isMobile ? 2 : 1}>AI Usage</Title>
				</Group>
				<div className="mb-6">
					<Anchor href="/admin">&larr; Back to Admin</Anchor>
				</div>

				{/* Filters */}
				<SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} mb="md">
					<TextInput
						placeholder="Search by user..."
						leftSection={<IconSearch size={16} />}
						value={search}
						onChange={(e) => setSearch(e.currentTarget.value)}
					/>
					<Select
						placeholder="All organizations"
						data={orgOptions}
						value={selectedOrgId}
						onChange={setSelectedOrgId}
						searchable
						onSearchChange={setOrgSearch}
						clearable
						nothingFoundMessage="No organizations found"
					/>
					<MultiSelect
						placeholder="All operations"
						data={operationOptions}
						value={selectedOperations}
						onChange={setSelectedOperations}
						clearable
						searchable
					/>
					<DatePickerInput
						type="range"
						placeholder="Date range"
						value={dateRange}
						onChange={setDateRange}
						clearable
					/>
				</SimpleGrid>

				{/* Summary Cards */}
				{summary && (
					<SimpleGrid cols={{ base: 2, sm: 4 }} mb="md">
						<Card shadow="sm" padding="md" radius="md" withBorder>
							<Text size="xs" c="dimmed" tt="uppercase" fw={700}>
								Total Calls
							</Text>
							<Text size="xl" fw={700}>
								{formatNumber(summary.totalCalls)}
							</Text>
						</Card>
						<Card shadow="sm" padding="md" radius="md" withBorder>
							<Text size="xs" c="dimmed" tt="uppercase" fw={700}>
								Total Tokens
							</Text>
							<Text size="xl" fw={700}>
								{formatNumber(summary.totalTokens)}
							</Text>
							<Text size="xs" c="dimmed">
								{formatNumber(summary.totalPromptTokens)} prompt /{" "}
								{formatNumber(summary.totalCompletionTokens)} completion
							</Text>
						</Card>
						<Card shadow="sm" padding="md" radius="md" withBorder>
							<Text size="xs" c="dimmed" tt="uppercase" fw={700}>
								Total Cost
							</Text>
							<Text size="xl" fw={700}>
								{formatCost(summary.totalCostUsd)}
							</Text>
						</Card>
						<Card shadow="sm" padding="md" radius="md" withBorder>
							<Text size="xs" c="dimmed" tt="uppercase" fw={700}>
								Avg Duration
							</Text>
							<Text size="xl" fw={700}>
								{Math.round(summary.avgDurationMs)}ms
							</Text>
						</Card>
					</SimpleGrid>
				)}

				{/* Data */}
				{loading ? (
					<div className="flex items-center justify-center py-8">
						<Loader size="lg" />
					</div>
				) : items.length === 0 ? (
					<Card shadow="sm" padding="xl" radius="md" withBorder>
						<Text size="lg" c="dimmed" ta="center">
							{filter
								? "No AI usage found matching filters."
								: "No AI usage recorded yet."}
						</Text>
					</Card>
				) : isMobile ? (
					<Stack gap="md">
						{items.map((item) => (
							<Card
								key={item.id}
								shadow="sm"
								padding="md"
								radius="md"
								withBorder
							>
								<Stack gap="xs">
									<Group justify="space-between" align="flex-start">
										<Text fw={600} size="sm">
											{item.operation}
										</Text>
										<Text size="xs" c="dimmed">
											{item.model}
										</Text>
									</Group>
									{item.user ? (
										<Link
											to="/admin/users/$userId"
											params={{ userId: item.user.id }}
											style={{ textDecoration: "none" }}
										>
											<Text size="sm" c="blue">
												{item.user.displayName}
											</Text>
										</Link>
									) : (
										<Text size="sm" c="dimmed">
											Deleted user
										</Text>
									)}
									<Link
										to="/admin/organizations/$organizationId"
										params={{ organizationId: item.organization.id }}
										style={{ textDecoration: "none" }}
									>
										<Text size="sm" c="blue">
											{item.organization.name}
										</Text>
									</Link>
									<Group justify="space-between">
										<Text size="xs" c="dimmed">
											{formatNumber(item.totalTokens)} tokens
										</Text>
										<Text size="xs" c="dimmed">
											{Math.round(item.durationMs)}ms
										</Text>
										<Text size="xs" fw={600}>
											{formatCost(item.costUsd)}
										</Text>
									</Group>
									<Tooltip label={new Date(item.createdAt).toLocaleString()}>
										<Text size="xs" c="dimmed" style={{ cursor: "default" }}>
											{new Date(item.createdAt).toLocaleDateString()}
										</Text>
									</Tooltip>
								</Stack>
							</Card>
						))}
					</Stack>
				) : (
					<Card shadow="sm" padding={0} radius="md" withBorder>
						<ScrollArea>
							<Table striped highlightOnHover>
								<Table.Thead>
									<Table.Tr>
										<Table.Th>Date</Table.Th>
										<Table.Th>User</Table.Th>
										<Table.Th>Organization</Table.Th>
										<Table.Th>Operation</Table.Th>
										<Table.Th>Model</Table.Th>
										<Table.Th style={{ textAlign: "right" }}>Prompt</Table.Th>
										<Table.Th style={{ textAlign: "right" }}>
											Completion
										</Table.Th>
										<Table.Th style={{ textAlign: "right" }}>Total</Table.Th>
										<Table.Th style={{ textAlign: "right" }}>Duration</Table.Th>
										<Table.Th style={{ textAlign: "right" }}>Cost</Table.Th>
									</Table.Tr>
								</Table.Thead>
								<Table.Tbody>
									{items.map((item) => (
										<Table.Tr key={item.id}>
											<Table.Td>
												<Tooltip
													label={new Date(item.createdAt).toLocaleString()}
												>
													<Text
														size="sm"
														style={{ cursor: "default", whiteSpace: "nowrap" }}
													>
														{new Date(item.createdAt).toLocaleDateString()}
													</Text>
												</Tooltip>
											</Table.Td>
											<Table.Td>
												{item.user ? (
													<Link
														to="/admin/users/$userId"
														params={{ userId: item.user.id }}
														style={{ textDecoration: "none" }}
													>
														<Text size="sm">{item.user.displayName}</Text>
													</Link>
												) : (
													<Text size="sm" c="dimmed">
														Deleted user
													</Text>
												)}
											</Table.Td>
											<Table.Td>
												<Link
													to="/admin/organizations/$organizationId"
													params={{
														organizationId: item.organization.id,
													}}
													style={{ textDecoration: "none" }}
												>
													<Text size="sm">{item.organization.name}</Text>
												</Link>
											</Table.Td>
											<Table.Td>
												<Text size="sm">{item.operation}</Text>
											</Table.Td>
											<Table.Td>
												<Text size="sm">{item.model}</Text>
											</Table.Td>
											<Table.Td style={{ textAlign: "right" }}>
												<Text size="sm">{formatNumber(item.promptTokens)}</Text>
											</Table.Td>
											<Table.Td style={{ textAlign: "right" }}>
												<Text size="sm">
													{formatNumber(item.completionTokens)}
												</Text>
											</Table.Td>
											<Table.Td style={{ textAlign: "right" }}>
												<Text size="sm">{formatNumber(item.totalTokens)}</Text>
											</Table.Td>
											<Table.Td style={{ textAlign: "right" }}>
												<Text size="sm">{Math.round(item.durationMs)}ms</Text>
											</Table.Td>
											<Table.Td style={{ textAlign: "right" }}>
												<Text size="sm" fw={600}>
													{formatCost(item.costUsd)}
												</Text>
											</Table.Td>
										</Table.Tr>
									))}
								</Table.Tbody>
							</Table>
						</ScrollArea>
					</Card>
				)}

				{totalCount > 0 && (
					<Group justify="center" mt="md">
						{totalPages > 1 && (
							<Pagination
								value={page}
								onChange={setPage}
								total={totalPages}
								size={isMobile ? "sm" : "md"}
							/>
						)}
						<Text size="sm" c="dimmed">
							{totalCount} call{totalCount !== 1 && "s"} total
						</Text>
					</Group>
				)}
			</Container>
		</div>
	);
}
